import axios from "axios";
import { getTenantHost, initTenantHost } from "../utils/tenant";

initTenantHost();

export const API_URL =
  process.env.REACT_APP_API_URL || "http://localhost:5000/api";
// Base URL for static files (without /api)
export const BASE_URL = API_URL.replace(/\/api\/?$/, "");

/** Slug folder upload dari hostname tenant, mis. edukasihalalindonesia.web.id */
export const getTenantSlug = () => {
  const host = getTenantHost();
  if (!host) return "default";
  return host.replace(/[^a-zA-Z0-9.-]/g, "-");
};

/**
 * URL publik file upload.
 * Path DB bisa format lama (slider/file.jpg) atau baru (slug/slider/file.jpg).
 * Production memakai path relatif (/uploads/...) agar tidak mixed-content HTTPS/HTTP.
 */
export const getUploadUrl = (relativePath) => {
  if (!relativePath) return "";
  let p = String(relativePath).replace(/^\/+/, "");

  // Path lama tanpa prefix slug tenant
  if (!/^[\w.-]+\.[\w.-]+\//.test(p)) {
    p = `${getTenantSlug()}/${p}`;
  }

  const configured = (process.env.REACT_APP_API_URL || "").trim();
  if (!configured || configured.startsWith("/")) {
    return `/uploads/${p}`;
  }

  const uploadsBase = configured.replace(/\/api\/?$/, "");
  return `${uploadsBase}/uploads/${p}`;
};

/** Normalisasi src gambar di HTML editor: hapus host localhost/salah, pakai path /uploads/ */
export const normalizeContentImageUrls = (html) => {
  if (!html) return html;
  return html.replace(
    /https?:\/\/[^"'\s>]+?(\/uploads\/[^"'\s>]+)/gi,
    "$1",
  );
};

/** URL gambar hasil upload berita/content dari response API */
export const getBeritaUploadImageUrl = (data) => {
  if (!data) return "";
  if (data.filename) return getUploadUrl(data.filename);
  if (data.url?.startsWith("/uploads/")) return data.url;
  if (data.url) return `${BASE_URL}${data.url.startsWith("/") ? "" : "/"}${data.url}`;
  return data.fullUrl || "";
};

/** URL file pendamping (KTP/ijazah/foto) via backend — proxy ke uploads lokal atau SIHALAL */
const appendTenantHostParam = (url) => {
  const tenantHost = getTenantHost();
  if (!url || !tenantHost || String(url).startsWith("data:")) return url;

  try {
    if (/^https?:\/\//i.test(url)) {
      const parsed = new URL(url);
      parsed.searchParams.set("tenant_host", tenantHost);
      return parsed.toString();
    }

    const [path, hash = ""] = String(url).split("#");
    const separator = path.includes("?") ? "&" : "?";
    const query = `tenant_host=${encodeURIComponent(tenantHost)}`;
    return `${path}${separator}${query}${hash ? `#${hash}` : ""}`;
  } catch {
    const separator = String(url).includes("?") ? "&" : "?";
    return `${url}${separator}tenant_host=${encodeURIComponent(tenantHost)}`;
  }
};

export const getPendampingFileUrl = (folder, filename) => {
  if (!filename) return "";
  const trimmed = String(filename).trim();
  if (!trimmed) return "";
  if (trimmed.startsWith("data:") || /^https?:\/\//i.test(trimmed)) {
    return trimmed;
  }
  const configured = (process.env.REACT_APP_API_URL || "").trim();
  if (!configured || configured.startsWith("/")) {
    return appendTenantHostParam(
      `/api/pendamping/file/${folder}/${encodeURIComponent(trimmed)}`,
    );
  }
  const apiBase = configured.replace(/\/api\/?$/, "");
  return appendTenantHostParam(
    `${apiBase}/api/pendamping/file/${folder}/${encodeURIComponent(trimmed)}`,
  );
};

/** Pilih URL dokumen — abaikan fullUrl backend jika rusak (base64 tercampur ke path SIHALAL) */
export const resolvePendampingDocUrl = (folder, filename, fullUrl) => {
  const trimmed = filename ? String(filename).trim() : "";
  if (trimmed.startsWith("data:") || /^https?:\/\//i.test(trimmed)) {
    return trimmed;
  }
  if (fullUrl && !/\/photo\/[^/]+\/data:/i.test(fullUrl)) {
    return appendTenantHostParam(fullUrl);
  }
  return getPendampingFileUrl(folder, filename);
};

const api = axios.create({
  baseURL: API_URL,
  headers: {
    "Content-Type": "application/json",
  },
});

const applyTenantHeader = (config) => {
  const tenantHost = getTenantHost();
  if (tenantHost) {
    config.headers = config.headers || {};
    config.headers["X-Tenant-Host"] = tenantHost;
  }
  return config;
};

// Semua request axios (termasuk import langsung di halaman admin) kirim tenant
axios.interceptors.request.use(applyTenantHeader);
api.interceptors.request.use(applyTenantHeader);

/** Ambil pesan error yang jelas dari respons API atau error jaringan. */
export const getApiErrorMessage = (error, fallback) => {
  if (!error) return fallback;

  if (!error.response) {
    if (error.code === "ECONNABORTED") {
      return "Permintaan timeout. Periksa koneksi internet dan coba lagi.";
    }
    return "Tidak dapat terhubung ke server. Periksa koneksi internet Anda.";
  }

  const data = error.response.data;
  if (typeof data === "string" && data.trim()) return data.trim();

  const message = data?.message;
  const detail = data?.error;

  if (message && detail && message !== detail) {
    const generic = /^(error submitting|internal server error|request failed)/i;
    if (generic.test(message) && typeof detail === "string") {
      return detail.length <= 200 ? detail : message;
    }
    return message;
  }

  if (message) return message;
  if (typeof detail === "string" && detail) return detail;

  const status = error.response.status;
  if (status === 413) return "Ukuran data terlalu besar. Perkecil ukuran foto KTP/ijazah.";
  if (status === 401) return "Sesi habis. Silakan login kembali.";
  if (status === 403) return "Anda tidak memiliki akses untuk aksi ini.";
  if (status >= 500) return "Server sedang bermasalah. Coba lagi atau hubungi admin.";

  return fallback;
};

/** Host website tenant — re-export dari utils/tenant.js */
export { getTenantHost, initTenantHost } from "../utils/tenant";

const attachTenantHeader = (config = {}) => {
  const tenantHost = getTenantHost();
  if (!tenantHost) return config;
  return {
    ...config,
    headers: {
      ...config.headers,
      "X-Tenant-Host": tenantHost,
    },
  };
};

const tenantRequestConfig = attachTenantHeader;

// Auth API
export const login = (data) => api.post("/auth/login", data);
export const verifyUser = (data) => api.post("/auth/verify", data);
export const changePassword = (data) => api.post("/auth/change-password", data);
export const resetPassword = (data) => api.post("/auth/reset-password", data);

// Slider API
export const getSliders = () => api.get("/slider");
export const getSliderById = (id) => api.get(`/slider/${id}`);
export const createSlider = (formData) =>
  api.post("/slider", formData, {
    headers: { "Content-Type": "multipart/form-data" },
  });
export const updateSlider = (id, formData) =>
  api.put(`/slider/${id}`, formData, {
    headers: { "Content-Type": "multipart/form-data" },
  });
export const deleteSlider = (id) => api.delete(`/slider/${id}`);

// Client API
export const getClients = () => api.get("/client");
export const getClientById = (id) => api.get(`/client/${id}`);
export const createClient = (formData) =>
  api.post("/client", formData, {
    headers: { "Content-Type": "multipart/form-data" },
  });
export const updateClient = (id, formData) =>
  api.put(`/client/${id}`, formData, {
    headers: { "Content-Type": "multipart/form-data" },
  });
export const deleteClient = (id) => api.delete(`/client/${id}`);

// Content API
export const getAllContent = () => api.get("/content");
export const getContentByJenis = (jenis) => api.get(`/content/${jenis}`);
export const updateContent = (data) => api.post("/content", data);
export const deleteContent = (id) => api.delete(`/content/${id}`);
export const uploadContentFile = (formData) =>
  axios.post(
    `${API_URL}/content/upload-file`,
    formData,
    tenantRequestConfig({
      headers: { "Content-Type": "multipart/form-data" },
    }),
  );

// Berita API
export const getAllBerita = () => api.get("/berita");
export const getBeritaById = (id) => api.get(`/berita/${id}`);
export const createBerita = (data) => api.post("/berita", data);
export const updateBerita = (id, data) => api.put(`/berita/${id}`, data);
export const deleteBerita = (id) => api.delete(`/berita/${id}`);
export const uploadBeritaImage = (formData) =>
  api.post("/berita/upload-image", formData, {
    headers: { "Content-Type": "multipart/form-data" },
  });

// Contact API
export const submitContact = (data) => api.post("/contact", data);
export const getContacts = () => api.get("/contact");
export const deleteContact = (id) => api.delete(`/contact/${id}`);

// Pendamping/P3H API
export const submitPendaftaran = (formData) =>
  api.post("/pendamping", formData, {
    headers: { "Content-Type": "multipart/form-data" },
  });
export const getAllPendaftaran = () => api.get("/pendamping");
export const getCalonPendamping = () => api.get("/pendamping/calon-peserta");
export const getPendaftaranById = (id) => api.get(`/pendamping/${id}`);
export const getProfilPendampingSaya = (userid) =>
  api.get("/pendamping/profil-saya", { params: { userid } });
export const getPengajuanPendampingList = (idPendamping) =>
  api.get(`/pengajuan/${idPendamping}`);
export const updatePendaftaran = (id, formData) =>
  axios.put(
    `${API_URL}/pendamping/${id}`,
    formData,
    tenantRequestConfig({
      headers: { "Content-Type": "multipart/form-data" },
    }),
  );
export const updatePendampingReferral = (id, data) =>
  api.patch(`/pendamping/${id}/referral`, data);
export const checkNikPendamping = (nik) =>
  api.get(`/pendamping/check-nik/${nik}`);
export const checkEmailPendamping = (email) =>
  api.get(`/pendamping/check-email/${encodeURIComponent(email)}`);
export const ajukanUlang = (id_pendamping) =>
  api.post("/pendamping/ajukan-ulang", { id_pendamping });

// Reference API
export const getProvinsi = () => api.get("/reference/provinsi");
export const getKabupaten = (kodeprovinsi) =>
  api.get(`/reference/kabupaten/${kodeprovinsi}`);
export const getKecamatan = (kodekabupaten) =>
  api.get(`/reference/kecamatan/${kodekabupaten}`);
export const getBanks = () => api.get("/reference/banks");
export const getRefByGroup = (group_id) =>
  api.get(`/reference/ref/${group_id}`);

// Menu API
export const getMenus = () => api.get("/menu");
export const getAdminMenus = (options = {}) => {
  const params = options.bustCache ? { _: Date.now() } : undefined;
  return api.get("/menu/admin", { params });
};
export const createMenu = (data) => api.post("/menu", data);
export const updateMenu = (id, data) => api.put(`/menu/${id}`, data);
export const deleteMenu = (id) => api.delete(`/menu/${id}`);

// Tahap Pelatihan API
export const getTahapPelatihanList = () => api.get(`/tahap-pelatihan`);
export const getTahapPelatihanById = (id) => api.get(`/tahap-pelatihan/${id}`);
export const createTahapPelatihan = (formData) =>
  axios.post(
    `${API_URL}/tahap-pelatihan`,
    formData,
    tenantRequestConfig({
      headers: { "Content-Type": "multipart/form-data" },
    }),
  );
export const updateTahapPelatihan = (id, formData) =>
  axios.put(
    `${API_URL}/tahap-pelatihan/${id}`,
    formData,
    tenantRequestConfig({
      headers: { "Content-Type": "multipart/form-data" },
    }),
  );
export const deleteTahapPelatihan = (id) =>
  api.delete(`/tahap-pelatihan/${id}`);

// LP Settings API
export const getLPSettings = () => api.get("/lp/settings");
export const getRekapLembaga = () => api.get("/lp/rekap-lembaga");

// Tenant public config (termasuk LOGO_URL)
export const getConfig = () => api.get("/config");

// Fetch registrasi info by no_daftar via backend proxy route
export const getInfoRegistrasiByNoDaftar = (no_daftar) =>
  api.get(
    `/pendamping/info-registrasi-by-nodaftar?no_daftar=${encodeURIComponent(no_daftar)}`,
  );

// SIHALAL helper: cek pembayaran via backend proxy
export const getCekPembayaran = (no_daftar) =>
  api.get(`/sihalal/cek-pembayaran?no_daftar=${encodeURIComponent(no_daftar)}`);

// SIHALAL helper: get invoice lembaga pelatihan by lembaga (LPK_ID from backend env)
export const getInvoiceLembagaPelatihan = () =>
  api.get(`/sihalal/invoice-lembaga-pelatihan`);

// SIHALAL helper: get sertifikat penyelia by lembaga (LPK_ID from backend env)
export const getSertifikatPenyeliaByLembaga = (jenisPelatihan, angkatan) => {
  const params = new URLSearchParams();
  if (jenisPelatihan) params.append("jenis_pelatihan", jenisPelatihan);
  if (angkatan) params.append("angkatan", angkatan);
  const qs = params.toString() ? `?${params.toString()}` : "";
  return api.get(`/sihalal/sertifikat-penyelia-by-lembaga${qs}`);
};

// Jadwal pelatihan: get distinct angkatan by jenis_pelatihan
export const getAngkatanByJenisPelatihan = (jenis_pelatihan) =>
  api.get(
    `/jadwal-pelatihan/angkatan?jenis_pelatihan=${encodeURIComponent(jenis_pelatihan)}`,
  );

export const getNamaByJenisAngkatan = (jenis_pelatihan, angkatan) =>
  api.get(
    `/pendaftaran-penyelia/nama-by-jenis-angkatan?jenis_pelatihan=${encodeURIComponent(jenis_pelatihan)}&angkatan=${encodeURIComponent(angkatan)}`,
  );

// SIHALAL helper: get sertifikat auditor by lembaga (LPK_ID from backend env)
export const getSertifikatAuditorByLembaga = (angkatan) => {
  const params = new URLSearchParams();
  if (angkatan) params.append("angkatan", angkatan);
  const qs = params.toString() ? `?${params.toString()}` : "";
  return api.get(`/sihalal/sertifikat-auditor-by-lembaga${qs}`);
};

// SIHALAL helper: get sertifikat JPH lainnya by lembaga (LPK_ID from backend env)
export const getSertifikatJulehaByLembaga = (jenisPelatihan, angkatan) => {
  const params = new URLSearchParams();
  if (jenisPelatihan) params.append("jenis_pelatihan", jenisPelatihan);
  if (angkatan) params.append("angkatan", angkatan);
  const qs = params.toString() ? `?${params.toString()}` : "";
  return api.get(`/sihalal/sertifikat-juleha-by-lembaga${qs}`);
};

// SIHALAL helper: get list sertifikat penyelia
export const getSertifikatPenyeliaList = (angkatan) => {
  const params = new URLSearchParams();
  if (angkatan) params.append("angkatan", angkatan);
  const qs = params.toString() ? `?${params.toString()}` : "";
  return api.get(`/sihalal/sertifikat-penyelia-list${qs}`);
};

// SIHALAL helper: get list sertifikat auditor
export const getSertifikatAuditorList = (angkatan) => {
  const params = new URLSearchParams();
  if (angkatan) params.append("angkatan", angkatan);
  const qs = params.toString() ? `?${params.toString()}` : "";
  return api.get(`/sihalal/sertifikat-auditor-list${qs}`);
};

// SIHALAL helper: get list sertifikat JPH lainnya
export const getSertifikatJulehaList = (angkatan) => {
  const params = new URLSearchParams();
  if (angkatan) params.append("angkatan", angkatan);
  const qs = params.toString() ? `?${params.toString()}` : "";
  return api.get(`/sihalal/sertifikat-juleha-list${qs}`);
};

// SIHALAL helper: insert sertifikat penyelia via backend proxy
export const insertSertifikatPenyelia = (formData) =>
  axios.post(`${API_URL}/sihalal/sertifikat-penyelia-insert`, formData, {
    headers: { "Content-Type": "multipart/form-data" },
  });

// SIHALAL helper: update sertifikat penyelia status via backend proxy
export const updateSertifikatPenyelia = (data) => {
  const isMultipart =
    typeof FormData !== "undefined" && data instanceof FormData;

  if (isMultipart) {
    return axios.put(`${API_URL}/sihalal/sertifikat-penyelia-update`, data, {
      headers: { "Content-Type": "multipart/form-data" },
    });
  }

  return api.put(`/sihalal/sertifikat-penyelia-update`, data);
};

// SIHALAL helper: delete sertifikat penyelia via backend proxy
export const deleteSertifikatPenyelia = (data) =>
  api.delete(`/sihalal/sertifikat-penyelia-delete`, { data });

// SIHALAL helper: insert sertifikat auditor via backend proxy
export const insertSertifikatAuditor = (formData) =>
  axios.post(`${API_URL}/sihalal/sertifikat-auditor-insert`, formData, {
    headers: { "Content-Type": "multipart/form-data" },
  });

// SIHALAL helper: update sertifikat auditor status via backend proxy
export const updateSertifikatAuditor = (data) => {
  const isMultipart =
    typeof FormData !== "undefined" && data instanceof FormData;

  if (isMultipart) {
    return axios.put(`${API_URL}/sihalal/sertifikat-auditor-update`, data, {
      headers: { "Content-Type": "multipart/form-data" },
    });
  }

  return api.put(`/sihalal/sertifikat-auditor-update`, data);
};

// SIHALAL helper: delete sertifikat auditor via backend proxy
export const deleteSertifikatAuditor = (data) =>
  api.delete(`/sihalal/sertifikat-auditor-delete`, { data });

// SIHALAL helper: insert sertifikat JPH lainnya via backend proxy
export const insertSertifikatJuleha = (formData) =>
  axios.post(`${API_URL}/sihalal/sertifikat-juleha-insert`, formData, {
    headers: { "Content-Type": "multipart/form-data" },
  });

// SIHALAL helper: update sertifikat JPH lainnya status via backend proxy
export const updateSertifikatJuleha = (data) => {
  const isMultipart =
    typeof FormData !== "undefined" && data instanceof FormData;

  if (isMultipart) {
    return axios.put(`${API_URL}/sihalal/sertifikat-juleha-update`, data, {
      headers: { "Content-Type": "multipart/form-data" },
    });
  }

  return api.put(`/sihalal/sertifikat-juleha-update`, data);
};

// SIHALAL helper: delete sertifikat JPH lainnya via backend proxy
export const deleteSertifikatJuleha = (data) =>
  api.delete(`/sihalal/sertifikat-juleha-delete`, { data });

// SIHALAL helper: get daftar audit
export const getDaftarAudit = () => api.get(`/sihalal/daftar-audit`);

// Kalkulator Biaya SH API
export const getKalkulatorProvinsi = () => api.get("/kalkulator/provinsi");
export const getKalkulatorKabupaten = (kodeprovinsi) =>
  api.get(
    `/kalkulator/kabupaten?kodeprovinsi=${encodeURIComponent(kodeprovinsi)}`,
  );
export const getKalkulatorJenisLayanan = () =>
  api.get("/kalkulator/jenis-layanan");
export const getKalkulatorJenisProduk = (kodelayanan) =>
  api.get(
    `/kalkulator/jenis-produk?kodelayanan=${encodeURIComponent(kodelayanan)}`,
  );
export const hitungBiayaSH = (data) => api.post("/kalkulator/hitung", data);

export default api;
